import React, { useEffect, useState } from 'react';
import {
  Row, Col, Card, Typography, Table, Progress, Statistic, Empty, Spin, Space, Button, message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchReports } from '../services/callService';

const { Title, Text } = Typography;

const downloadCsv = (name, cols, rows) => {
  if (!rows.length) { message.info('Nothing to export'); return; }
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `${name}_${dayjs().format('YYYYMMDD')}.csv`; a.click();
  URL.revokeObjectURL(url);
};

const CallReports = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => { try { setData(await fetchReports()); } finally { setLoading(false); } })(); }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (!data) return <Empty description="No report data" />;

  const completion = data.completion || {};
  const totalC = (Number(completion.completed) || 0) + (Number(completion.pending) || 0) + (Number(completion.missed) || 0);
  const completionRate = totalC ? Math.round((Number(completion.completed) / totalC) * 100) : 0;
  const maxLost = Math.max(1, ...(data.lostReasons || []).map(r => Number(r.n)));

  return (
    <div>
      <Title level={4} style={{ margin: '4px 8px 16px' }}>Reports &amp; Analytics</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card bordered={false} style={{ borderRadius: 8 }}><Statistic title="Completed" value={Number(completion.completed) || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={12} md={6}><Card bordered={false} style={{ borderRadius: 8 }}><Statistic title="Pending" value={Number(completion.pending) || 0} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
        <Col xs={12} md={6}><Card bordered={false} style={{ borderRadius: 8 }}><Statistic title="Missed" value={Number(completion.missed) || 0} valueStyle={{ color: '#f5222d' }} /></Card></Col>
        <Col xs={12} md={6}><Card bordered={false} style={{ borderRadius: 8 }}>
          <Text type="secondary">Completion Rate</Text>
          <Progress percent={completionRate} strokeColor="#52c41a" />
        </Card></Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Executive Performance" bordered={false} style={{ borderRadius: 8 }}
            extra={<Button size="small" icon={<DownloadOutlined />} onClick={() => downloadCsv('executive_performance', ['ExecutiveName', 'calls', 'converted', 'avgDuration'], data.byExec || [])} />}>
            <Table size="small" pagination={false} rowKey="ExecutiveName" dataSource={data.byExec || []}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              columns={[
                { title: 'Executive', dataIndex: 'ExecutiveName' },
                { title: 'Calls', dataIndex: 'calls', align: 'center' },
                { title: 'Converted', dataIndex: 'converted', align: 'center' },
                { title: 'Avg Dur (min)', dataIndex: 'avgDuration', align: 'center' },
              ]} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Lost Reasons" bordered={false} style={{ borderRadius: 8 }}>
            {(data.lostReasons || []).length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No lost leads" /> : (
              <Space direction="vertical" style={{ width: '100%' }}>
                {data.lostReasons.map(r => (
                  <div key={r.LostReason}>
                    <Text>{r.LostReason} <Text type="secondary">({r.n})</Text></Text>
                    <Progress percent={Math.round((Number(r.n) / maxLost) * 100)} showInfo={false} strokeColor="#f5222d" />
                  </div>
                ))}
              </Space>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Follow-ups by Type" bordered={false} style={{ borderRadius: 8 }}>
            <Table size="small" pagination={false} rowKey="FollowupType" dataSource={data.byType || []}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              columns={[{ title: 'Type', dataIndex: 'FollowupType' }, { title: 'Count', dataIndex: 'n', align: 'center' }]} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Daily Calls (last 30 days)" bordered={false} style={{ borderRadius: 8 }}
            extra={<Button size="small" icon={<DownloadOutlined />} onClick={() => downloadCsv('daily_calls', ['d', 'calls', 'completed'], (data.daily || []).map(x => ({ ...x, d: dayjs(x.d).format('YYYY-MM-DD') })))} />}>
            <Table size="small" pagination={{ pageSize: 8 }} rowKey="d" dataSource={data.daily || []}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              columns={[
                { title: 'Date', dataIndex: 'd', render: (d) => dayjs(d).format('DD MMM YYYY') },
                { title: 'Calls', dataIndex: 'calls', align: 'center' },
                { title: 'Completed', dataIndex: 'completed', align: 'center' },
              ]} />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CallReports;
